import { transformError as sdkTransformError } from '@wix/sdk-runtime/transform-error';
import { queryBuilder } from '@wix/sdk-runtime/query-builder';
import {
  renameKeysFromSDKRequestToRESTRequest,
  renameKeysFromRESTResponseToSDKResponse,
} from '@wix/sdk-runtime/rename-all-nested-keys';
import {
  HttpClient,
  HttpResponse,
  NonNullablePaths,
  QuerySpec,
  Query as QuerySdkType,
} from '@wix/sdk-types';
import * as ambassadorWixEmailsTransmissionsV1EmailTransmission from './emails-transmissions-v1-email-transmission-email-transmissions.http.js';
// @ts-ignore
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { createQueryUtils } from '@wix/sdk-runtime/query-builder-utils';

/**
 * An email transmission represents a single email send request,
 * including its recipients, HTML content, sender information, type, and delivery status.
 * Creating an email transmission triggers the email to be sent asynchronously to all specified recipients.
 */
export interface EmailTransmission
  extends EmailTransmissionTypeOptionsOneOf,
    EmailTransmissionStatusOptionsOneOf {
  /**
   * Options for marketing emails.
   *
   * Used only when `type` is `MARKETING`.
   */
  marketingOptions?: MarketingOptions;
  /**
   * Details about why the email transmission was rejected.
   *
   * Returned only when `status` is `REJECTED`.
   * @readonly
   */
  rejectedOptions?: RejectedOptions;
  /**
   * Email transmission ID.
   * @format GUID
   * @readonly
   */
  _id?: string;
  /**
   * Date and time the email transmission was created.
   * @readonly
   */
  _createdDate?: Date | null;
  /**
   * Date and time the email transmission was last updated.
   * @readonly
   */
  _updatedDate?: Date | null;
  /**
   * Revision number, which increments by 1 each time the email transmission is updated.
   * @readonly
   */
  revision?: string | null;
  /**
   * Subject line of the email.
   * @maxLength 200
   */
  emailSubject?: string;
  /**
   * Full HTML content of the email.
   *
   * Not returned in `queryEmailTransmissions`. Use `getEmailTransmission` to retrieve the HTML content of a specific transmission.
   *
   * Stored for 30 days after the transmission is created. After that, this field returns `"<expired>"`.
   * @maxLength 1000000
   */
  emailHtmlContent?: string;
  /**
   * Custom key-value pairs attached to the transmission to identify the context in which the email was sent.
   * These values are passed to the email service provider and included in domain event payloads.
   * Keys and values support alphanumeric characters only (letters and numbers, no spaces or special characters).
   *
   * Default: `{}`
   * @maxSize 10
   */
  metadata?: Record<string, string>;
  /**
   * Display name shown as the sender.
   *
   * Default: None (no display name is set).
   * @minLength 1
   * @maxLength 50
   */
  senderName?: string | null;
  /**
   * Sender email address. Must be verified using the Sender Emails API before use.
   *
   * If the provided address can't be used as a FROM address (for example, addresses from free email providers
   * such as gmail.com), the email is sent from `no-reply@wixsitemail.com` and the provided address is
   * automatically used as the reply-to address instead.
   *
   * Default: `no-reply@wixsitemail.com`
   * @format EMAIL
   */
  senderEmailAddress?: string | null;
  /**
   * Recipient that receives replies to this email. When a recipient replies, the reply is directed to this
   * address instead of the sender address.
   *
   * Default: When `senderEmailAddress` is provided and the email is sent from the Wix shared sender address,
   * replies are automatically directed to `senderEmailAddress`.
   */
  replyTo?: Recipient;
  /**
   * List of recipients the email is sent to.
   * @minSize 1
   * @maxSize 100
   */
  toRecipients?: Recipient[];
  /** Email type. */
  type?: TypeWithLiterals;
  /**
   * Current status of the email transmission.
   * @readonly
   */
  status?: StatusWithLiterals;
}

/** @oneof */
export interface EmailTransmissionTypeOptionsOneOf {
  /**
   * Options for marketing emails.
   *
   * Used only when `type` is `MARKETING`.
   */
  marketingOptions?: MarketingOptions;
}

/** @oneof */
export interface EmailTransmissionStatusOptionsOneOf {
  /**
   * Details about why the email transmission was rejected.
   *
   * Returned only when `status` is `REJECTED`.
   * @readonly
   */
  rejectedOptions?: RejectedOptions;
}

/**
 * A recipient identifies a single addressee for an email transmission.
 * A recipient can be specified by contact ID, user ID, or raw email address.
 */
export interface Recipient extends RecipientValueOneOf {
  /**
   * ID of the contact to send the email to. The contact's email address is retrieved from the Contacts API.
   * @format GUID
   */
  contactId?: string;
  /**
   * ID of the Wix user to send the email to. The user must be a site collaborator.
   *
   * The user's email address is retrieved from the Users API.
   * @format GUID
   */
  userId?: string;
  /**
   * Email address to send the email to. A contact is created with this address if one doesn't already exist.
   * @format EMAIL
   */
  emailAddress?: string;
  /**
   * Display name of the recipient.
   * @minLength 1
   * @maxLength 50
   */
  name?: string | null;
  /**
   * Status of this particular recipient.
   * @readonly
   */
  status?: RecipientStatusWithLiterals;
  /**
   * Reason why the email couldn't be delivered to this recipient. `NONE` means the email was successfully sent.
   *
   * Returned once the transmission reaches `PROCESSED` status.
   * @readonly
   */
  failureReason?: FailureReasonWithLiterals;
}

/** @oneof */
export interface RecipientValueOneOf {
  /**
   * ID of the contact to send the email to. The contact's email address is retrieved from the Contacts API.
   * @format GUID
   */
  contactId?: string;
  /**
   * ID of the Wix user to send the email to. The user must be a site collaborator.
   *
   * The user's email address is retrieved from the Users API.
   * @format GUID
   */
  userId?: string;
  /**
   * Email address to send the email to. A contact is created with this address if one doesn't already exist.
   * @format EMAIL
   */
  emailAddress?: string;
}

export enum RecipientStatus {
  /** Recipient is still waiting to be processed. */
  PENDING = 'PENDING',
  /** Email for this recipient was sent (it's not the same as "delivered" because it can still bounce). */
  SENT = 'SENT',
  /** Recipient was excluded (see "failureReason" for more details). */
  FAILED = 'FAILED',
}

/** @enumType */
export type RecipientStatusWithLiterals =
  | RecipientStatus
  | 'PENDING'
  | 'SENT'
  | 'FAILED';

export enum FailureReason {
  /** Email was successfully sent to this recipient. */
  NONE = 'NONE',
  /** Contact or user wasn't found by the provided ID. */
  RECIPIENT_NOT_FOUND = 'RECIPIENT_NOT_FOUND',
  /** Contact or user doesn't have an email address. */
  EMAIL_ADDRESS_MISSING = 'EMAIL_ADDRESS_MISSING',
  /** Email address doesn't exist or is invalid. */
  EMAIL_ADDRESS_INVALID = 'EMAIL_ADDRESS_INVALID',
  /** Email address has previously bounced and can't be sent to. */
  EMAIL_ADDRESS_BOUNCED = 'EMAIL_ADDRESS_BOUNCED',
  /** Recipient hasn't given consent to receive marketing emails. */
  MARKETING_CONSENT_NOT_GIVEN = 'MARKETING_CONSENT_NOT_GIVEN',
  /** User isn't a collaborator on this site. Only site collaborators can be specified as recipients by user ID. */
  NOT_SITE_COLLABORATOR = 'NOT_SITE_COLLABORATOR',
}

/** @enumType */
export type FailureReasonWithLiterals =
  | FailureReason
  | 'NONE'
  | 'RECIPIENT_NOT_FOUND'
  | 'EMAIL_ADDRESS_MISSING'
  | 'EMAIL_ADDRESS_INVALID'
  | 'EMAIL_ADDRESS_BOUNCED'
  | 'MARKETING_CONSENT_NOT_GIVEN'
  | 'NOT_SITE_COLLABORATOR';

export enum Type {
  /** Email triggered by a specific action, such as an order confirmation or booking reminder. No unsubscribe link is added. */
  TRANSACTIONAL = 'TRANSACTIONAL',
  /** Email sent to multiple recipients as part of a campaign, such as a newsletter or promotion. An unsubscribe link is automatically added. */
  MARKETING = 'MARKETING',
}

/** @enumType */
export type TypeWithLiterals = Type | 'TRANSACTIONAL' | 'MARKETING';

/** Options for marketing email transmissions. */
export interface MarketingOptions {
  /**
   * Placeholder in `emailHtmlContent` that gets replaced with a recipient-specific unsubscribe URL before the email is sent. Case-sensitive.
   * For example, `{{unsubscribe_url}}` or `[UNSUBSCRIBE_URL]`. The placeholder must be unique within the HTML content.
   *
   * If not set, an unsubscribe link in the site's default language is appended to the bottom of the email.
   * @minLength 4
   * @maxLength 100
   */
  unsubscribeUrlPlaceholder?: string | null;
}

export enum Status {
  /** Email transmission was accepted and is queued for processing. */
  ACCEPTED = 'ACCEPTED',
  /** Email transmission is being processed. The content is being checked, recipients are being resolved, and emails are being sent. */
  IN_PROGRESS = 'IN_PROGRESS',
  /** Email transmission has been fully processed. Per-recipient delivery information is available in `toRecipients`. */
  PROCESSED = 'PROCESSED',
  /** Email transmission failed an abuse check. No emails were sent. See `rejectedOptions` for details. */
  REJECTED = 'REJECTED',
}

/** @enumType */
export type StatusWithLiterals =
  | Status
  | 'ACCEPTED'
  | 'IN_PROGRESS'
  | 'PROCESSED'
  | 'REJECTED';

/** Details about a rejected email transmission. */
export interface RejectedOptions {
  /**
   * Reasons why the email transmission was rejected.
   * The blacklists are managed by Wix and aren't queryable.
   * If you believe your content was flagged incorrectly, contact Wix support.
   * @minSize 1
   * @maxSize 10
   * @readonly
   */
  rejectionReasons?: RejectionReasonWithLiterals[];
}

export enum RejectionReason {
  /** Sender email address is on the Wix blacklist. */
  BLACKLISTED_SENDER_EMAIL = 'BLACKLISTED_SENDER_EMAIL',
  /** Sender display name is on the Wix blacklist. */
  BLACKLISTED_SENDER_NAME = 'BLACKLISTED_SENDER_NAME',
  /** Reply-to email address is on the Wix blacklist. */
  BLACKLISTED_REPLY_TO_EMAIL = 'BLACKLISTED_REPLY_TO_EMAIL',
  /** Reply-to display name is on the Wix blacklist. */
  BLACKLISTED_REPLY_TO_NAME = 'BLACKLISTED_REPLY_TO_NAME',
  /** Email content contains a link that is on the Wix blacklist. */
  BLACKLISTED_LINK = 'BLACKLISTED_LINK',
  /** Email content contains text that is on the Wix blacklist. */
  BLACKLISTED_TEXT = 'BLACKLISTED_TEXT',
  /** Email content was identified as fraudulent, such as phishing or a scam. */
  FRAUDULENT_CONTENT = 'FRAUDULENT_CONTENT',
}

/** @enumType */
export type RejectionReasonWithLiterals =
  | RejectionReason
  | 'BLACKLISTED_SENDER_EMAIL'
  | 'BLACKLISTED_SENDER_NAME'
  | 'BLACKLISTED_REPLY_TO_EMAIL'
  | 'BLACKLISTED_REPLY_TO_NAME'
  | 'BLACKLISTED_LINK'
  | 'BLACKLISTED_TEXT'
  | 'FRAUDULENT_CONTENT';

export interface GetLiveEmailTransmissionRequest {
  /**
   * MetaSite ID that the email transmission belongs to.
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * Email transmission ID.
   * @format GUID
   */
  emailTransmissionId?: string;
}

export interface GetLiveEmailTransmissionResponse {
  /** Retrieved email transmission. */
  emailTransmission?: EmailTransmission;
}

export interface GetDeletedEmailTransmissionRequest {
  /**
   * MetaSite ID that the deleted email transmission belongs to.
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * Email transmission ID.
   * @format GUID
   */
  emailTransmissionId?: string;
}

export interface GetDeletedEmailTransmissionResponse {
  /** Retrieved deleted email transmission. */
  emailTransmission?: EmailTransmission;
}

export interface QueryLiveEmailTransmissionsRequest {
  /**
   * MetaSite ID to query email transmissions from.
   * @format GUID
   */
  metaSiteId?: string;
  /** Query options, including filter, sorting, and pagination. */
  query?: CursorQuery;
}

export interface CursorQuery extends CursorQueryPagingMethodOneOf {
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`. */
  cursorPaging?: CursorPaging;
  /**
   * Filter object in the following format:
   * `"filter" : {
   * "fieldName1": "value1",
   * "fieldName2":{"$operator":"value2"}
   * }`
   * Example of operators: `$eq`, `$ne`, `$lt`, `$lte`, `$gt`, `$gte`, `$in`, `$hasSome`, `$hasAll`, `$startsWith`, `$contains`
   */
  filter?: Record<string, any> | null;
  /**
   * Sort object in the following format:
   * `[{"fieldName":"sortField1","order":"ASC"},{"fieldName":"sortField2","order":"DESC"}]`
   * @maxSize 4
   */
  sort?: Sorting[];
}

/** @oneof */
export interface CursorQueryPagingMethodOneOf {
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`. */
  cursorPaging?: CursorPaging;
}

export interface Sorting {
  /**
   * Name of the field to sort by.
   * @minLength 1
   * @maxLength 50
   */
  fieldName?: string;
  /** Sort order. */
  order?: SortOrderWithLiterals;
}

export enum SortOrder {
  ASC = 'ASC',
  DESC = 'DESC',
}

/** @enumType */
export type SortOrderWithLiterals = SortOrder | 'ASC' | 'DESC';

export interface CursorPaging {
  /**
   * Number of items to load.
   * @max 100
   */
  limit?: number | null;
  /**
   * Pointer to the next or previous page in the list of results.
   *
   * You can get the relevant cursor token
   * from the `pagingMetadata` object in the previous call's response.
   * Not relevant for the first request.
   * @maxLength 1000
   */
  cursor?: string | null;
}

export interface QueryLiveEmailTransmissionsResponse {
  /**
   * Retrieved email transmissions.
   * @maxSize 100
   */
  emailTransmissions?: EmailTransmission[];
  /** Paging metadata. */
  pagingMetadata?: CursorPagingMetadata;
}

export interface CursorPagingMetadata {
  /** Number of items returned in the response. */
  count?: number | null;
  /** Offset that was requested. */
  cursors?: Cursors;
  /**
   * Indicates if there are more results after the current page.
   * If `true`, another page of results can be retrieved.
   * If `false`, this is the last page.
   */
  hasNext?: boolean | null;
}

export interface Cursors {
  /**
   * Cursor pointing to next page in the list of results.
   * @maxLength 1000
   */
  next?: string | null;
  /**
   * Cursor pointing to previous page in the list of results.
   * @maxLength 1000
   */
  prev?: string | null;
}

export interface QueryDeletedEmailTransmissionsRequest {
  /**
   * MetaSite ID to query deleted email transmissions from.
   * @format GUID
   */
  metaSiteId?: string;
  /** Query options, including filter, sorting, and pagination. */
  query?: CursorQuery;
}

export interface QueryDeletedEmailTransmissionsResponse {
  /**
   * Retrieved deleted email transmissions.
   * @maxSize 100
   */
  emailTransmissions?: EmailTransmission[];
  /** Paging metadata. */
  pagingMetadata?: CursorPagingMetadata;
}

export interface DomainEvent extends DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  _id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at `16:00` and then again at `16:01`, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
}

/** @oneof */
export interface DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
}

export interface EntityCreatedEvent {
  entity?: string;
}

export interface RestoreInfo {
  deletedDate?: Date | null;
}

export interface EntityUpdatedEvent {
  /**
   * Since platformized APIs only expose PATCH and not PUT we can't assume that the fields sent from the client are the actual diff.
   * This means that to generate a list of changed fields (as opposed to sent fields) one needs to traverse both objects.
   * We don't want to impose this on all developers and so we leave this traversal to the notification recipients which need it.
   */
  currentEntity?: string;
}

export interface EntityDeletedEvent {
  /** Entity that was deleted. */
  deletedEntity?: string | null;
}

export interface ActionEvent {
  body?: string;
}

export interface MessageEnvelope {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Stringify payload. */
  data?: string;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /** @readonly */
  identityType?: WebhookIdentityTypeWithLiterals;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}

export enum WebhookIdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

/** @enumType */
export type WebhookIdentityTypeWithLiterals =
  | WebhookIdentityType
  | 'UNKNOWN'
  | 'ANONYMOUS_VISITOR'
  | 'MEMBER'
  | 'WIX_USER'
  | 'APP';

export interface AccountInfo {
  /**
   * ID of the Wix account associated with the event.
   * @format GUID
   */
  accountId?: string | null;
  /**
   * ID of the parent Wix account. Only included when accountId belongs to a child account.
   * @format GUID
   */
  parentAccountId?: string | null;
  /**
   * ID of the Wix site associated with the event. Only included when the event is tied to a specific site.
   * @format GUID
   */
  siteId?: string | null;
}

export interface SendEmailTransmissionRequest {
  /** Email transmission to send. */
  emailTransmission: EmailTransmission;
  /**
   * Unique key used to prevent duplicate transmissions. If a request with the same idempotency key is received,
   * the original response is returned without creating a new transmission.
   * @format GUID
   */
  idempotencyKey?: string | null;
}

export interface SendEmailTransmissionResponse {
  /** Sent email transmission. */
  emailTransmission?: EmailTransmission;
}

export interface AccountSuspendedErrorDetails {
  /**
   * URL of the page where the site owner can contact support or learn more about the account suspension.
   * @format WEB_URL
   */
  termsOfServicePageUrl?: string;
}

export interface OutOfQuotaErrorDetails {
  /**
   * URL of the page where the site owner can upgrade their Email Marketing plan.
   * @format WEB_URL
   */
  packagePickerUrl?: string;
}

export interface GetEmailTransmissionRequest {
  /**
   * Email transmission ID.
   * @format GUID
   */
  emailTransmissionId: string;
}

export interface GetEmailTransmissionResponse {
  /** Retrieved email transmission. */
  emailTransmission?: EmailTransmission;
}

export interface QueryEmailTransmissionsRequest {
  /** Query options, including filter, sorting, and pagination. */
  query?: CursorQuery;
}

export interface QueryEmailTransmissionsResponse {
  /** Retrieved email transmissions. */
  emailTransmissions?: EmailTransmission[];
  /** Paging metadata. */
  pagingMetadata?: CursorPagingMetadata;
}

export interface DeleteEmailTransmissionRequest {
  /**
   * Email transmission ID.
   * @format GUID
   */
  emailTransmissionId: string;
}

export interface DeleteEmailTransmissionResponse {}

export interface Empty {}

/** @docsIgnore */
export type SendEmailTransmissionApplicationErrors =
  | {
      code?: 'ACCOUNT_SUSPENDED';
      description?: string;
      data?: AccountSuspendedErrorDetails;
    }
  | {
      code?: 'UNVERIFIED_SENDER_EMAIL';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'OUT_OF_QUOTA';
      description?: string;
      data?: OutOfQuotaErrorDetails;
    };
/** @docsIgnore */
export type SendEmailTransmissionValidationErrors = {
  ruleName?: 'INVALID_METADATA';
};
/** @docsIgnore */
export type DeleteEmailTransmissionApplicationErrors = {
  code?: 'EMAIL_TRANSMISSION_IS_IN_PROGRESS';
  description?: string;
  data?: Record<string, any>;
};

export interface BaseEventMetadata {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface EventMetadata extends BaseEventMetadata {
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  _id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at `16:00` and then again at `16:01`, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
  accountInfo?: AccountInfoMetadata;
}

export interface AccountInfoMetadata {
  /** ID of the Wix account associated with the event */
  accountId: string;
  /** ID of the Wix site associated with the event. Only included when the event is tied to a specific site. */
  siteId?: string;
  /** ID of the parent Wix account. Only included when 'accountId' belongs to a child account. */
  parentAccountId?: string;
}

export interface EmailTransmissionCreatedEnvelope {
  entity: EmailTransmission;
  metadata: EventMetadata;
}

/**
 * Triggered when an email transmission is created.
 * @permissionScope Manage Stores
 * @permissionScopeId SCOPE.STORES.MANAGE-STORES
 * @permissionScope Access Verticals by Automations
 * @permissionScopeId SCOPE.CRM.ACCESS-VERTICALS-BY-AUTOMATIONS
 * @permissionScope Set Up Automations
 * @permissionScopeId SCOPE.CRM.SETUP-AUTOMATIONS
 * @permissionScope Manage Email Marketing
 * @permissionScopeId SCOPE.DC-PROMOTE.EMAIL-MARKETING
 * @permissionId shoutout.manage
 * @webhook
 * @eventType wix.emails.transmissions.v1.email_transmission_created
 * @slug created
 * @documentationMaturity preview
 */
export declare function onEmailTransmissionCreated(
  handler: (event: EmailTransmissionCreatedEnvelope) => void | Promise<void>
): void;

export interface EmailTransmissionDeletedEnvelope {
  metadata: EventMetadata;
}

/**
 * Triggered when an email transmission is deleted.
 * @permissionScope Manage Stores
 * @permissionScopeId SCOPE.STORES.MANAGE-STORES
 * @permissionScope Access Verticals by Automations
 * @permissionScopeId SCOPE.CRM.ACCESS-VERTICALS-BY-AUTOMATIONS
 * @permissionScope Set Up Automations
 * @permissionScopeId SCOPE.CRM.SETUP-AUTOMATIONS
 * @permissionScope Manage Email Marketing
 * @permissionScopeId SCOPE.DC-PROMOTE.EMAIL-MARKETING
 * @permissionId shoutout.manage
 * @webhook
 * @eventType wix.emails.transmissions.v1.email_transmission_deleted
 * @slug deleted
 * @documentationMaturity preview
 */
export declare function onEmailTransmissionDeleted(
  handler: (event: EmailTransmissionDeletedEnvelope) => void | Promise<void>
): void;

export interface EmailTransmissionUpdatedEnvelope {
  entity: EmailTransmission;
  metadata: EventMetadata;
  /** @hidden */
  modifiedFields: Record<string, any>;
}

/**
 * Triggered when an email transmission is updated.
 * @permissionScope Manage Stores
 * @permissionScopeId SCOPE.STORES.MANAGE-STORES
 * @permissionScope Access Verticals by Automations
 * @permissionScopeId SCOPE.CRM.ACCESS-VERTICALS-BY-AUTOMATIONS
 * @permissionScope Set Up Automations
 * @permissionScopeId SCOPE.CRM.SETUP-AUTOMATIONS
 * @permissionScope Manage Email Marketing
 * @permissionScopeId SCOPE.DC-PROMOTE.EMAIL-MARKETING
 * @permissionId shoutout.manage
 * @webhook
 * @eventType wix.emails.transmissions.v1.email_transmission_updated
 * @slug updated
 * @documentationMaturity preview
 */
export declare function onEmailTransmissionUpdated(
  handler: (event: EmailTransmissionUpdatedEnvelope) => void | Promise<void>
): void;

/**
 * Creates and sends an email transmission to one or more recipients.
 *
 * The email is sent asynchronously. The returned transmission has `ACCEPTED` status.
 * Call [Get Email Transmission](https://dev.wix.com/docs/api-reference/business-management/marketing/emails/email-transmission-v1/get-email-transmission) or listen for the `EmailTransmissionCreated` webhook to track delivery status.
 * @param emailTransmission - Email transmission to send.
 * @public
 * @documentationMaturity preview
 * @requiredField emailTransmission
 * @requiredField emailTransmission.emailHtmlContent
 * @requiredField emailTransmission.emailSubject
 * @requiredField emailTransmission.toRecipients
 * @requiredField emailTransmission.type
 * @permissionId shoutout.manage
 * @applicableIdentity APP
 * @fqn wix.emails.transmissions.v1.EmailTransmissionService.SendEmailTransmission
 */
export async function sendEmailTransmission(
  emailTransmission: NonNullablePaths<
    EmailTransmission,
    `emailHtmlContent` | `emailSubject` | `toRecipients` | `type`,
    2
  >,
  options?: SendEmailTransmissionOptions
): Promise<
  NonNullablePaths<
    SendEmailTransmissionResponse,
    | `emailTransmission.rejectedOptions.rejectionReasons`
    | `emailTransmission._id`
    | `emailTransmission.emailSubject`
    | `emailTransmission.emailHtmlContent`
    | `emailTransmission.replyTo.contactId`
    | `emailTransmission.replyTo.userId`
    | `emailTransmission.replyTo.emailAddress`
    | `emailTransmission.replyTo.status`
    | `emailTransmission.replyTo.failureReason`
    | `emailTransmission.toRecipients`
    | `emailTransmission.type`
    | `emailTransmission.status`,
    4
  > & {
    __applicationErrorsType?: SendEmailTransmissionApplicationErrors;
    __validationErrorsType?: SendEmailTransmissionValidationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    emailTransmission: emailTransmission,
    idempotencyKey: options?.idempotencyKey,
  });

  const reqOpts =
    ambassadorWixEmailsTransmissionsV1EmailTransmission.sendEmailTransmission(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          emailTransmission: '$[0]',
          idempotencyKey: '$[1].idempotencyKey',
        },
        singleArgumentUnchanged: false,
      },
      ['emailTransmission', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface SendEmailTransmissionOptions {
  /**
   * Unique key used to prevent duplicate transmissions. If a request with the same idempotency key is received,
   * the original response is returned without creating a new transmission.
   * @format GUID
   */
  idempotencyKey?: string | null;
}

/**
 * Retrieves an email transmission by ID.
 * @param emailTransmissionId - Email transmission ID.
 * @public
 * @documentationMaturity preview
 * @requiredField emailTransmissionId
 * @permissionId shoutout.read
 * @applicableIdentity APP
 * @returns Retrieved email transmission.
 * @fqn wix.emails.transmissions.v1.EmailTransmissionService.GetEmailTransmission
 */
export async function getEmailTransmission(
  emailTransmissionId: string
): Promise<
  NonNullablePaths<
    EmailTransmission,
    | `rejectedOptions.rejectionReasons`
    | `_id`
    | `emailSubject`
    | `emailHtmlContent`
    | `replyTo.contactId`
    | `replyTo.userId`
    | `replyTo.emailAddress`
    | `replyTo.status`
    | `replyTo.failureReason`
    | `toRecipients`
    | `type`
    | `status`,
    3
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    emailTransmissionId: emailTransmissionId,
  });

  const reqOpts =
    ambassadorWixEmailsTransmissionsV1EmailTransmission.getEmailTransmission(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)
      ?.emailTransmission!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { emailTransmissionId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['emailTransmissionId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Retrieves a list of up to 100 email transmissions, given the provided paging, filtering, and sorting.
 *
 * `emailHtmlContent` is not returned in the results. To retrieve the full HTML content of a specific transmission,
 * call [Get Email Transmission](https://dev.wix.com/docs/api-reference/business-management/marketing/emails/email-transmission-v1/get-email-transmission).
 *
 * Query Email Transmissions runs with these defaults, which you can override:
 *
 * - `updatedDate` is sorted in `DESC` order
 * - `paging.limit` is `50`
 *
 * To learn about working with Query methods, see [API Query Language](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-the-wix-api-query-language),
 * [Sorting and Paging](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-sorting-and-paging),
 * and [Field Projection](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-field-projection).
 * @public
 * @documentationMaturity preview
 * @permissionId shoutout.read
 * @applicableIdentity APP
 * @fqn wix.emails.transmissions.v1.EmailTransmissionService.QueryEmailTransmissions
 */
export function queryEmailTransmissions(): EmailTransmissionsQueryBuilder {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[0] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  return queryBuilder<
    EmailTransmission,
    'CURSOR',
    QueryEmailTransmissionsRequest,
    QueryEmailTransmissionsResponse
  >({
    func: async (payload: QueryEmailTransmissionsRequest) => {
      const reqOpts =
        ambassadorWixEmailsTransmissionsV1EmailTransmission.queryEmailTransmissions(
          payload
        );

      sideEffects?.onSiteCall?.();
      try {
        const result = await httpClient.request(reqOpts);
        sideEffects?.onSuccess?.(result);
        return result;
      } catch (err) {
        sideEffects?.onError?.(err);
        throw err;
      }
    },
    requestTransformer: (query: QueryEmailTransmissionsRequest['query']) => {
      const args = [query, {}] as [QueryEmailTransmissionsRequest['query'], {}];
      return renameKeysFromSDKRequestToRESTRequest({
        ...args?.[1],
        query: args?.[0],
      });
    },
    responseTransformer: ({
      data,
    }: HttpResponse<QueryEmailTransmissionsResponse>) => {
      const transformedData = renameKeysFromRESTResponseToSDKResponse(
        transformPaths(data, [])
      );

      return {
        items: transformedData?.emailTransmissions,
        pagingMetadata: transformedData?.pagingMetadata,
      };
    },
    errorTransformer: (err: unknown) => {
      const transformedError = sdkTransformError(err, {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { query: '$[0]' },
        singleArgumentUnchanged: false,
      });

      throw transformedError;
    },
    pagingMethod: 'CURSOR',
    transformationPaths: {},
  });
}

interface QueryCursorResult {
  cursors: Cursors;
  hasNext: () => boolean;
  hasPrev: () => boolean;
  length: number;
  pageSize: number;
}

export interface EmailTransmissionsQueryResult extends QueryCursorResult {
  items: EmailTransmission[];
  query: EmailTransmissionsQueryBuilder;
  next: () => Promise<EmailTransmissionsQueryResult>;
  prev: () => Promise<EmailTransmissionsQueryResult>;
}

export interface EmailTransmissionsQueryBuilder {
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  eq: (
    propertyName: '_id' | '_updatedDate' | 'emailSubject' | 'type' | 'status',
    value: any
  ) => EmailTransmissionsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  ne: (
    propertyName: '_id' | '_updatedDate' | 'emailSubject' | 'type' | 'status',
    value: any
  ) => EmailTransmissionsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  ge: (
    propertyName: '_updatedDate' | 'emailSubject',
    value: any
  ) => EmailTransmissionsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  gt: (
    propertyName: '_updatedDate' | 'emailSubject',
    value: any
  ) => EmailTransmissionsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  le: (
    propertyName: '_updatedDate' | 'emailSubject',
    value: any
  ) => EmailTransmissionsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  lt: (
    propertyName: '_updatedDate' | 'emailSubject',
    value: any
  ) => EmailTransmissionsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `string`.
   * @param string - String to compare against. Case-insensitive.
   * @documentationMaturity preview
   */
  startsWith: (
    propertyName: 'emailSubject',
    value: string
  ) => EmailTransmissionsQueryBuilder;
  /** @documentationMaturity preview */
  in: (
    propertyName: '_id' | '_updatedDate' | 'emailSubject' | 'type' | 'status',
    value: any
  ) => EmailTransmissionsQueryBuilder;
  /** @documentationMaturity preview */
  exists: (
    propertyName: '_updatedDate' | 'emailSubject',
    value: boolean
  ) => EmailTransmissionsQueryBuilder;
  /** @param propertyNames - Properties used in the sort. To sort by multiple properties, pass properties as additional arguments.
   * @documentationMaturity preview
   */
  ascending: (
    ...propertyNames: Array<
      '_id' | '_updatedDate' | 'emailSubject' | 'type' | 'status'
    >
  ) => EmailTransmissionsQueryBuilder;
  /** @param propertyNames - Properties used in the sort. To sort by multiple properties, pass properties as additional arguments.
   * @documentationMaturity preview
   */
  descending: (
    ...propertyNames: Array<
      '_id' | '_updatedDate' | 'emailSubject' | 'type' | 'status'
    >
  ) => EmailTransmissionsQueryBuilder;
  /** @param limit - Number of items to return, which is also the `pageSize` of the results object.
   * @documentationMaturity preview
   */
  limit: (limit: number) => EmailTransmissionsQueryBuilder;
  /** @param cursor - A pointer to specific record
   * @documentationMaturity preview
   */
  skipTo: (cursor: string) => EmailTransmissionsQueryBuilder;
  /** @documentationMaturity preview */
  find: () => Promise<EmailTransmissionsQueryResult>;
}

/**
 * @hidden
 * @fqn wix.emails.transmissions.v1.EmailTransmissionService.QueryEmailTransmissions
 * @requiredField query
 */
export async function typedQueryEmailTransmissions(
  query: EmailTransmissionQuery
): Promise<
  NonNullablePaths<
    QueryEmailTransmissionsResponse,
    | `emailTransmissions`
    | `emailTransmissions.${number}._id`
    | `emailTransmissions.${number}.emailSubject`
    | `emailTransmissions.${number}.emailHtmlContent`
    | `emailTransmissions.${number}.replyTo.contactId`
    | `emailTransmissions.${number}.replyTo.userId`
    | `emailTransmissions.${number}.replyTo.emailAddress`
    | `emailTransmissions.${number}.replyTo.status`
    | `emailTransmissions.${number}.replyTo.failureReason`
    | `emailTransmissions.${number}.type`
    | `emailTransmissions.${number}.status`,
    5
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ query: query });

  const reqOpts =
    ambassadorWixEmailsTransmissionsV1EmailTransmission.queryEmailTransmissions(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { query: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['query']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface EmailTransmissionQuerySpec extends QuerySpec {
  paging: 'cursor';
  wql: [
    {
      fields: ['_id', 'status', 'type'];
      operators: ['$eq', '$in', '$ne'];
      sort: 'BOTH';
    },
    {
      fields: ['_updatedDate', 'emailSubject'];
      operators: '*';
      sort: 'BOTH';
    }
  ];
}

export type CommonQueryWithEntityContext = QuerySdkType<
  EmailTransmission,
  EmailTransmissionQuerySpec
>;
export type EmailTransmissionQuery = {
  /** 
  Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`.  
  */
  cursorPaging?: {
    /** 
  Number of items to load. 
  @max: 100 
  */
    limit?:
      | NonNullable<CommonQueryWithEntityContext['cursorPaging']>['limit']
      | null;
    /** 
  Pointer to the next or previous page in the list of results.

  You can get the relevant cursor token
  from the `pagingMetadata` object in the previous call's response.
  Not relevant for the first request. 
  @maxLength: 1000 
  */
    cursor?:
      | NonNullable<CommonQueryWithEntityContext['cursorPaging']>['cursor']
      | null;
  };
  /** 
  Filter object in the following format:
  `"filter" : {
  "fieldName1": "value1",
  "fieldName2":{"$operator":"value2"}
  }`
  Example of operators: `$eq`, `$ne`, `$lt`, `$lte`, `$gt`, `$gte`, `$in`, `$hasSome`, `$hasAll`, `$startsWith`, `$contains`  
  */
  filter?: CommonQueryWithEntityContext['filter'] | null;
  /** 
  Sort object in the following format:
  `[{"fieldName":"sortField1","order":"ASC"},{"fieldName":"sortField2","order":"DESC"}]` 
  @maxSize: 4 
  */
  sort?: {
    /** 
  Name of the field to sort by. 
  @minLength: 1,
  @maxLength: 50 
  */
    fieldName?: NonNullable<
      CommonQueryWithEntityContext['sort']
    >[number]['fieldName'];
    /** 
  Sort order.  
  */
    order?: NonNullable<CommonQueryWithEntityContext['sort']>[number]['order'];
  }[];
};

export const utils = {
  query: /*#__PURE__*/ createQueryUtils<
    EmailTransmission,
    EmailTransmissionQuerySpec,
    EmailTransmissionQuery
  >(),
};

/**
 * Deletes an email transmission by ID.
 *
 * Deletion is only allowed for transmissions with `PROCESSED` or `REJECTED` status.
 * @param emailTransmissionId - Email transmission ID.
 * @public
 * @documentationMaturity preview
 * @requiredField emailTransmissionId
 * @permissionId shoutout.manage
 * @applicableIdentity APP
 * @fqn wix.emails.transmissions.v1.EmailTransmissionService.DeleteEmailTransmission
 */
export async function deleteEmailTransmission(
  emailTransmissionId: string
): Promise<
  void & {
    __applicationErrorsType?: DeleteEmailTransmissionApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    emailTransmissionId: emailTransmissionId,
  });

  const reqOpts =
    ambassadorWixEmailsTransmissionsV1EmailTransmission.deleteEmailTransmission(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { emailTransmissionId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['emailTransmissionId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}
